import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import type { BrandProfileReview, BrandProfileReviewPage, BrandRatingReply } from '../../../api/payment/brand-profile.api';
import { BRAND_PROFILE_COPY, REPLY_MAX_LENGTH, reviewMonthLabel, validateReplyText } from '../../../utils/brand-profile';
import { apiErrorMessage } from '../../../utils/api-error-message';

export interface BrandReviewListProps {
  /** Phải ổn định (useCallback): đổi hàm là tải lại từ trang 1. */
  load: (page: number) => Promise<BrandProfileReviewPage>;
  canReply: (review: BrandProfileReview) => boolean;
  sendReply: (ratingId: string, text: string) => Promise<BrandRatingReply>;
  replyAuthorLabel: string;
}

// ─── Thành phần con khai ở cấp module để không remount (mất chữ đang gõ) ───

const ReplyBox: React.FC<{ onSend: (text: string) => Promise<boolean> }> = ({ onSend }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const invalid = !!validateReplyText(text);

  const submit = async () => {
    const error = validateReplyText(text);
    if (error) {
      toast.error(error);
      return;
    }
    setBusy(true);
    try {
      if (await onSend(text.trim())) setText('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 flex flex-col gap-1">
      <textarea
        aria-label="Trả lời đánh giá"
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={REPLY_MAX_LENGTH}
        rows={2}
        placeholder={BRAND_PROFILE_COPY.replyPlaceholder}
        className="w-full resize-none rounded-lg border ui-divider bg-transparent px-3 py-2 text-[13px] ui-text outline-none focus:border-[#0057FF]"
      />
      <div className="ui-muted flex items-center justify-between gap-2 text-[12px]">
        <span>{BRAND_PROFILE_COPY.replyOnceHint}</span>
        <span className="shrink-0">
          {text.trim().length}/{REPLY_MAX_LENGTH}
        </span>
      </div>
      <button
        type="button"
        disabled={busy || invalid}
        onClick={submit}
        className="self-end rounded-md bg-[#0057FF] px-3 py-1.5 text-[13px] font-semibold text-white disabled:opacity-50"
      >
        {BRAND_PROFILE_COPY.replyButton}
      </button>
    </div>
  );
};

const ReviewItem: React.FC<{
  review: BrandProfileReview;
  showReplyBox: boolean;
  replyAuthorLabel: string;
  onReply: (ratingId: string, text: string) => Promise<boolean>;
}> = ({ review, showReplyBox, replyAuthorLabel, onReply }) => (
  <div className="rounded-lg border ui-divider p-3 text-[13px]">
    <div className="font-semibold text-amber-500">
      {'★'.repeat(review.stars)}
      <span className="ui-muted">{'★'.repeat(5 - review.stars)}</span>
    </div>
    {review.comment && <p className="ui-text mt-1 whitespace-pre-wrap">{review.comment}</p>}
    <div className="ui-muted mt-1 text-[12px]">
      {`${review.campaignTitle} · ${reviewMonthLabel(review.createdAt)} · ${review.raterName}`}
    </div>
    {review.reply && (
      <div className="mt-2 rounded-md bg-slate-50 p-2 dark:bg-slate-800">
        <div className="ui-text text-[12px] font-semibold">{replyAuthorLabel}</div>
        <p className="ui-text whitespace-pre-wrap">{review.reply.text}</p>
      </div>
    )}
    {showReplyBox && <ReplyBox onSend={(text) => onReply(review.id, text)} />}
  </div>
);

/** Danh sách đánh giá đã công bố của một hồ sơ, mới nhất trước, "Xem thêm" để tải trang sau. */
const BrandReviewList: React.FC<BrandReviewListProps> = ({ load, canReply, sendReply, replyAuthorLabel }) => {
  const [items, setItems] = useState<BrandProfileReview[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setFailed(false);
    setItems([]);
    setPage(1);
    load(1)
      .then((res) => {
        if (!alive) return;
        setItems(res.items);
        setHasMore(res.hasMore);
      })
      .catch(() => {
        if (alive) setFailed(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [load]);

  const loadMore = async () => {
    const next = page + 1;
    setLoading(true);
    try {
      const res = await load(next);
      setItems((prev) => [...prev, ...res.items.filter((x) => !prev.some((p) => p.id === x.id))]);
      setHasMore(res.hasMore);
      setPage(next);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Không tải thêm được đánh giá.'));
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (ratingId: string, text: string): Promise<boolean> => {
    try {
      const reply = await sendReply(ratingId, text);
      setItems((prev) => prev.map((x) => (x.id === ratingId ? { ...x, reply, canReply: false } : x)));
      toast.success(BRAND_PROFILE_COPY.replySent);
      return true;
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Không gửi được trả lời.'));
      return false;
    }
  };

  if (failed) return <p className="ui-muted p-4 text-center text-[14px]">Không tải được đánh giá.</p>;
  if (loading && items.length === 0) {
    return (
      <div className="flex justify-center p-6">
        <Loader2 className="ui-muted animate-spin" />
      </div>
    );
  }
  if (items.length === 0) return <p className="ui-muted p-4 text-center text-[14px]">{BRAND_PROFILE_COPY.noReviews}</p>;

  return (
    <div className="flex flex-col gap-2">
      {items.map((r) => (
        <ReviewItem
          key={r.id}
          review={r}
          showReplyBox={!r.reply && canReply(r)}
          replyAuthorLabel={replyAuthorLabel}
          onReply={handleReply}
        />
      ))}
      {hasMore && (
        <button
          type="button"
          disabled={loading}
          onClick={loadMore}
          className="ui-text self-center rounded-md border ui-divider px-3 py-1 text-[14px] disabled:opacity-40"
        >
          {BRAND_PROFILE_COPY.loadMore}
        </button>
      )}
    </div>
  );
};

export default BrandReviewList;
